import React from 'react';
import { makeStyles } from '@mui/styles';
import PropTypes from "prop-types";
import { TextField, CircularProgress, Tooltip } from '@mui/material';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import { VariableSizeList } from 'react-window';
import useMediaQuery from '@mui/material/useMediaQuery';
import ListSubheader from '@mui/material/ListSubheader';
import { useTheme } from '@mui/styles';
import { Typography } from '@mui/material';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
	const { data, index, style } = props;
	const dataSet = data[index];
	const inlineStyle = {
		...style,
		top: style.top + LISTBOX_PADDING,
	};
	//console.error('dataSet');
	//console.error(dataSet);
	if (dataSet.hasOwnProperty('group')) {
		return (
			<ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
				{dataSet.group}
			</ListSubheader>
		);
	}

	const displayText = dataSet[1].parentname ? dataSet[1].parentname : dataSet[1].name;
	return (
		<Tooltip title={displayText} placement="right" arrow>
			<Typography component="li" {...dataSet[0]} noWrap style={inlineStyle}>
				{displayText}
			</Typography>
		</Tooltip>
	);
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
	const outerProps = React.useContext(OuterElementContext);
	return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
	const ref = React.useRef(null);
	React.useEffect(() => {
		if (ref.current != null) {
			ref.current.resetAfterIndex(0, true);
		}
	}, [data]);
	return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef(function ListboxComponent(props, ref) {
	const { children, ...other } = props;
	const itemData = [];
	children.forEach((item) => {
		itemData.push(item);
		itemData.push(...(item.children || []));
	});

	const theme = useTheme();
	const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
		noSsr: true,
	});

	const itemCount = itemData.length;
	const itemSize = smUp ? 36 : 48;

	const getChildSize = (child) => {
		if (child.hasOwnProperty('group')) {
			return 48;
		}

		return itemSize;
	};

	const getHeight = () => {
		if (itemCount > 8) {
			return 8 * itemSize;
		}
		return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
	};

	const gridRef = useResetCache(itemCount);

	return (
		<div ref={ref}>
			<OuterElementContext.Provider value={other}>
				<VariableSizeList
					itemData={itemData}
					height={getHeight() + 2 * LISTBOX_PADDING}
					width="100%"
					ref={gridRef}
					outerElementType={OuterElementType}
					innerElementType="ul"
					itemSize={(index) => getChildSize(itemData[index])}
					overscanCount={5}
					itemCount={itemCount}
				>
					{renderRow}
				</VariableSizeList>
			</OuterElementContext.Provider>
		</div>
	);
});

ListboxComponent.propTypes = {
	children: PropTypes.node,
};

const StyledPopper = styled(Popper)({
	[`& .${autocompleteClasses.listbox}`]: {
		boxSizing: 'border-box',
		'& ul': {
			padding: 0,
			margin: 0,
		},
	},
});

export default function SimpleAutoCompleteFullWidth(props) {

	const { label, options, stateName, changeSelect, selected, required, disabled, created = false, loading = false, fullWidth = false } = props;

	const defaultvalue = { id: 0, name: '' };

	const [opt, setOption] = React.useState(selected ? selected : defaultvalue);

	React.useEffect(() => {
		setOption(selected ? selected : defaultvalue);
	}, [selected]);

	const handleChange = (event, value) => {
		if (value == null) {
			value = { id: 0, name: '' };
		}
		setOption(value);
		changeSelect(stateName, value);
	};

	return (
		<Autocomplete
			id="combo-box"
			disablePortal
			options={options}
			autoHighlight
			value={opt}
			PopperComponent={StyledPopper}
			ListboxComponent={ListboxComponent}
			getOptionLabel={option => option.parentname ? option.parentname : option.name}
			getOptionDisabled={!created ? option => option.cancreatecalled === false : option => option}
			onChange={handleChange}
			fullWidth={fullWidth}
			sx={{ width: "100%" }}
			disabled={loading || disabled}
			renderInput={params => (
				<Tooltip title={opt && (opt.parentname || opt.name) ? (opt.parentname || opt.name) : ''} placement="top" arrow>
					<TextField
						{...params}
						label={label}
						variant="outlined"
					required={required != null ? required == false ? false : true : false}
					disabled={disabled}
					fullWidth
					InputLabelProps={{ shrink: true }}
					InputProps={{
						...params.InputProps,
						style: {
							height: 54,
							paddingRight: '66px', // ← valor mais justo para clear + arrow
						},
						endAdornment: (
							<>
								{loading
									? <CircularProgress color="inherit" size={20} style={{ marginRight: 4 }} />
									: null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
					sx={{
						'& .MuiAutocomplete-endAdornment': {
							top: '50%',
							transform: 'translateY(-50%)',
							right: '9px',
							display: 'flex',
							alignItems: 'center',
							gap: '1px', // ← reduz espaço entre os ícones
						},
						'& .MuiAutocomplete-endAdornment button': {
							padding: '2px', // ← reduz padding interno dos botões de ícone
						},
							'& .MuiInputBase-root': {
								alignItems: 'center',
							},
						}}
					/>
				</Tooltip>
			)}
			renderOption={(props, option, state) => [props, option, state.index]}
		/>
	);
}

SimpleAutoCompleteFullWidth.propTypes = {
	label: PropTypes.string,
	options: PropTypes.array,
	stateName: PropTypes.string,
	changeSelect: PropTypes.func,
	selected: PropTypes.object,
	checkbox: PropTypes.bool,
	created: PropTypes.bool
};